import React, {useEffect, useRef} from 'react';
import {Box, SpaceBetween, Spinner} from '@amzn/awsui-components-react';
import SupportPromptGroup from '@cloudscape-design/chat-components/support-prompt-group';
import {Message} from './types';
import moment from 'moment';
import {MarkdownMessage} from './markdown-message';

export interface ChatContainerProps {
    /** Array of messages to display in the conversation */
    messages: Message[];
    /** Whether the chatbot is currently processing a request */
    isLoading: boolean;
    /** Callback when a sample prompt is clicked */
    onPromptClick?: (prompt: string) => void;
}

/**
 * ChatContainer component displays the conversation history with messages,
 * welcome message, and loading indicator.
 *
 * Requirements:
 * - 3.1: Visually distinguish user messages from AI responses
 * - 3.2: Auto-scroll to show latest message
 * - 3.3: Display messages in chronological order (oldest first)
 * - 3.4: Include timestamps for each message
 * - 3.5: Display loading indicator during AI response generation
 * - 1.2: Display welcome message when no messages exist
 */
export function ChatContainer(props: ChatContainerProps) {
    const messageListRef = useRef<HTMLDivElement>(null);

    /**
     * Auto-scroll to the bottom when new messages are added
     * Requirement 3.2: Automatically scroll to show the latest message
     */
    useEffect(() => {
        if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
    }, [props.messages, props.isLoading]);

    /**
     * Check if there are any assistant messages in the conversation
     * Used to determine if sample prompts should be shown
     */
    const hasAssistantMessages = props.messages.some((message) => message.role === 'assistant');
    const hasUserMessages = props.messages.some((message) => message.role === 'user');

    /**
     * Renders the sample prompts positioned at bottom right
     * Only visible after AI sends first message but before user sends their first message
     */
    const renderSamplePrompts = () => {
        // Only show prompts after AI has sent a message but before user sends first message
        if (!hasAssistantMessages || hasUserMessages) {
            return null;
        }

        return (
            <div className="sample-prompts-container">
                <SupportPromptGroup
                    onItemClick={({detail}) => props.onPromptClick?.(detail.id)}
                    ariaLabel="Sample questions to get started"
                    items={[
                        {
                            text: "What changed from last week's forecast?",
                            id: "What changed from last week's forecast?",
                        },
                        {
                            text: 'How do I upload overrides?',
                            id: 'How do I upload overrides?',
                        },
                    ]}
                />
            </div>
        );
    };

    /**
     * Renders a single message bubble with appropriate styling based on role
     * Requirements:
     * - 3.1: Apply role-based styling (user vs assistant)
     * - 3.4: Display timestamp for each message
     */
    const renderMessage = (message: Message) => {
        const isUser = message.role === 'user';
        const bubbleClass = isUser ? 'message-bubble user-message' : 'message-bubble assistant-message';

        return (
            <div key={message.id} className={`message-wrapper ${isUser ? 'user-wrapper' : 'assistant-wrapper'}`}>
                <div className={bubbleClass} data-testid={`message-${message.role}`}>
                    <div className="message-header">
                        <Box fontSize="body-s" color="text-label" fontWeight="bold">
                            {isUser ? 'You' : 'Excelsior AI'}
                        </Box>
                    </div>
                    <div className="message-content">
                        <MarkdownMessage key={`${message.id}-${message.content.length}`} content={message.content} />
                    </div>
                    <div className="message-timestamp">
                        <Box fontSize="body-s" color="text-status-inactive">
                            {moment(message.timestamp).format('h:mm A')}
                        </Box>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Renders the loading indicator when AI is generating a response
     * Requirements: 3.5
     */
    const renderLoadingIndicator = () => {
        return (
            <div className="loading-indicator message-wrapper assistant-wrapper" data-testid="loading-indicator">
                <div className="message-bubble assistant-message">
                    <div className="message-header">
                        <Box fontSize="body-s" color="text-label" fontWeight="bold">
                            Excelsior AI
                        </Box>
                    </div>
                    <div className="message-content">
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                            <Spinner size="normal" />
                            <Box variant="p" color="text-body-secondary">
                                Thinking...
                            </Box>
                        </SpaceBetween>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="chat-container">
            <div className="message-list" ref={messageListRef} data-testid="message-list">
                {/* Requirement 3.3: Display messages in chronological order */}
                <SpaceBetween size="m" direction="vertical">
                    {props.messages.map((message) => renderMessage(message))}
                </SpaceBetween>

                {/* Requirement 3.5: Show loading indicator during API call */}
                {props.isLoading && renderLoadingIndicator()}
            </div>

            {/* Sample prompts positioned at bottom right, always visible */}
            {renderSamplePrompts()}
        </div>
    );
}
